import get from 'lodash/get'
import { l } from '@manychat/manyui'

import { fetchSubscriber, fetchSubscribers } from 'common/audience/actions/audienceActionsTyped'
import { UpgradeSource, billing } from 'common/billing'
import { alert } from 'common/core'
import { ProductType } from 'common/core/interfaces/products'
import { getCurrentAccount } from 'common/core/selectors/appSelectors'
import { getItems } from 'common/fields/selectors/userFieldsSelectors'
import { formatEntitiesListResponse } from 'common/usesList/utils'
import { UserField } from 'shared/api/common/schemas/userField'
import { UserFieldType } from 'shared/api/common/types'
import { UserFieldsApi } from 'shared/api/requests/customFields'
import { createAsyncAction } from 'shared/lib/redux'
import { RoutePaths } from 'utils/router/constants'
import { getPageId, getQuery, isPathnameMatchRoute } from 'utils/router/tools'
import { analyticsService } from 'utils/services/analytics'
import { getCurrentPage } from 'utils/url'

import { SYSTEM_FIELD_TOAST_MESSAGES, SystemFieldId } from '../constants/EditableSystemFields'
import { getUserFieldSearchValue } from '../selectors/userFieldsSelectors'

import { UserFieldState } from './types'

const namespace = 'userFields'

const USER_FIELDS_METRICS_MAP = {
  SETTINGS: {
    route: RoutePaths.SETTINGS,
    event: 'SETTINGS.CUSTOM_FIELD.CREATE',
  },
  AUDIENCE: {
    route: RoutePaths.AUDIENCE_SCOPE,
    event: 'AUDIENCE.CUSTOM_FIELD.CREATE',
  },
  CHAT: {
    route: RoutePaths.CHAT,
    event: 'LIVE_CHAT.CUSTOM_FIELD.CREATE',
  },
  CMS_BUILDER: {
    route: RoutePaths.CMS_BUILDER,
    event: 'FLOW.CUSTOM_FIELD.CREATE',
  },
}

export const fetchUserFields = createAsyncAction(
  `${namespace}/fetchUserFields`,
  async (params?: Partial<{ activeOnly: boolean; path: string; query: string }>) => {
    const { activeOnly = true, path, query } = params ?? {}
    const { data } = await UserFieldsApi.list({
      query: { active_only: activeOnly, path, query },
    })

    return data.fields
  },
)

export const createField = createAsyncAction(
  `${namespace}/createField`,
  async (_, { getState, dispatch }) => {
    const {
      title: caption,
      type,
      description,
      folderPath,
    } = getState().userFields.createModal as UserFieldState['createModal']

    const { data } = await UserFieldsApi.create({
      body: { caption, type, description, path: folderPath },
    })

    const currentRouteKey = Object.keys(USER_FIELDS_METRICS_MAP).find((routeKey) => {
      const pattern =
        USER_FIELDS_METRICS_MAP[routeKey as keyof typeof USER_FIELDS_METRICS_MAP].route
      return isPathnameMatchRoute(pattern)
    }) as keyof typeof USER_FIELDS_METRICS_MAP

    if (USER_FIELDS_METRICS_MAP[currentRouteKey]) {
      analyticsService.sendEvent(USER_FIELDS_METRICS_MAP[currentRouteKey].event)
    }

    const { path = '/' } = getQuery()
    const searchInputValue = getUserFieldSearchValue(getState())
    const fetchParams = searchInputValue ? { query: searchInputValue } : { path: path }

    dispatch(fetchUserFields({ activeOnly: false, ...fetchParams }))

    analyticsService.sendEvent('SESSION_QUALITY.SAVE.BOT')

    alert(l.translate('Field Created!'), 'success')

    return data.field
  },
  {
    onBusinessError: (err) => {
      if (billing.isProError(err)) {
        const message =
          err.error_data.reason ??
          l.translate(
            "You aren't able to activate this feature on the current plan. Upgrade for unlimited access.",
          )

        billing.handleProError(err, {
          message,
          title: l.translate('Create unlimited number of variables with Manychat Pro'),
          source: UpgradeSource.USER_FIELDS,
        })

        err.handle()
      }
    },
  },
)

const isChangesProhibited = (state: RootState) => {
  const { app, userFields } = state
  const max = get(app, 'freeVersionLimit.max_customFields_num', 3)
  const activeFields = userFields?.items?.filter((item) => item.status === 'active') ?? []
  const isProhibited = activeFields.length >= max && getCurrentAccount(state).isFree

  if (isProhibited) {
    billing.proAlert({
      message: l.translate(
        'You have reached the maximum number of active custom user fields on the current plan ({max}/{max}). Upgrade for unlimited access.',
        { max },
      ),
      title: l.translate('Create unlimited number of user fields with Manychat Pro'),
      source: UpgradeSource.USER_FIELDS,
      products: [ProductType.INBOX, ProductType.AUTOMATION],
    })
  }

  return isProhibited
}

export const openCreateFieldModal = createAsyncAction(
  `${namespace}/openCreateFieldModal`,
  async (
    payload: Partial<{
      builderId: number
      title: string
      type: string
      folderPath: string
      allowedFieldTypes: UserFieldType[]
    }>,
    { getState, rejectWithValue },
  ) => {
    // TODO: Refactor this logic. This check doesn't work properly when we have fields in folders.
    // In fields list we know only about fields from current folder, but not from other folders.
    // We can remove this check and use backend validation only, when it will be implemented.
    // TC-1686
    if (isChangesProhibited(getState())) {
      throw rejectWithValue()
    }

    return payload
  },
)

export const restoreField = createAsyncAction(
  `${namespace}/restoreField`,
  async (id: number, { getState, rejectWithValue }) => {
    // TODO: Refactor this logic. This check doesn't work properly when we have fields in folders.
    // In fields list we know only about fields from current folder, but not from other folders.
    // We can remove this check and use backend validation only, when it will be implemented.
    // TC-1686
    if (isChangesProhibited(getState())) {
      return rejectWithValue()
    }

    await UserFieldsApi.restore({ body: { field_id: id } })
    alert(l.translate('Field restored'), 'success')
    return
  },
)

export const ensureField = createAsyncAction(
  `${namespace}/ensureField`,
  async (
    { caption, type, description }: { caption: string; type: UserFieldType; description: string },
    { getState },
  ) => {
    const items = getItems(getState())
    const item = items.some((item: UserField) => item.caption === caption && item.type === type)

    if (item) {
      return item
    }

    const { data } = await UserFieldsApi.create({
      body: { caption, type, description, path: '/' },
    })

    return data.field
  },
)

export const saveField = createAsyncAction(
  `${namespace}/savedFieldNotification`,
  async ({ caption, description, field_id }: UserField) => {
    const { data } = await UserFieldsApi.save({
      body: { field_id, caption, description },
    })

    alert(l.translate('Field updated'), 'success')

    return data.field
  },
)

export const archiveField = createAsyncAction(
  `${namespace}/archiveField`,
  async (field: UserField) => {
    const { data } = await UserFieldsApi.archive({ body: { field_id: field.field_id } })

    alert(l.translate('Field archived'), 'success')
    return data.field
  },
)

export const deleteField = createAsyncAction(
  `${namespace}/deleteField`,
  async (id: number) => {
    await UserFieldsApi.delete({ body: { field_id: id } })
    alert(l.translate('Field deleted'), 'success')
  },
  {
    onBusinessError: (error) => {
      error.handle()
      alert(l.translate('Failed to delete field'), 'danger')
    },
  },
)
export const setUserFieldValue = createAsyncAction(
  `${namespace}/setUserFieldValue`,
  async ({
    field_id,
    value,
    user_id,
  }: {
    field_id: UserField['field_id']
    value?: unknown
    user_id: string
  }) => {
    analyticsService.sendEvent('LIVE_CHAT.CHANGE_CUF')
    await UserFieldsApi.setSubscriberField({
      body: {
        field_id,
        value: value ? String(value) : undefined,
        user_id,
      },
    })
  },
)

export const setSystemFieldValue = createAsyncAction(
  `${namespace}/setSystemFieldValue`,
  async (
    payload: {
      field: SystemFieldId
      user_id: string
      value: string
    },
    { dispatch },
  ) => {
    const { field, user_id, value } = payload
    const { data } = await UserFieldsApi.setSystemField({
      body: { field, user_id, value: value.trim() },
    })

    dispatch(fetchSubscriber(user_id))
    alert(l.getString(SYSTEM_FIELD_TOAST_MESSAGES[field]), 'success')

    const currentPage = getCurrentPage()

    if (currentPage) {
      let eventPage = ''
      if (currentPage === 'chat') {
        eventPage = 'LIVE_CHAT'
      } else if (currentPage === 'subscribers') {
        eventPage = 'AUDIENCE'
        dispatch(fetchSubscribers())
      }
      analyticsService.sendEvent(`${eventPage}.CHANGE_SUF`, {
        field,
      })
    }

    return data
  },
)

export const getEntitiesList = createAsyncAction(
  `${namespace}/getEntitiesList`,
  async (field_id: number) => {
    const pageId = getPageId()
    const { data } = await UserFieldsApi.getEntitiesList({ query: { field_id } })

    return formatEntitiesListResponse(data, pageId)
  },
)

export const bulkFieldsArchive = createAsyncAction(
  `${namespace}/bulkFieldsArchive`,
  async (ids: number[]) => {
    const { data } = await UserFieldsApi.archiveBulk({ body: { ids } })
    alert(l.translate('Fields archived'), 'success')
    return data.models
  },
  {
    onBusinessError: (error) => {
      error.handle()
      alert(l.translate('Failed to archive fields'), 'danger')
    },
  },
)

export const bulkFieldsDelete = createAsyncAction(
  `${namespace}/bulkFieldsDelete`,
  async (ids: number[]) => {
    const { data } = await UserFieldsApi.deleteBulk({ body: { ids } })
    alert(l.translate('Fields deleted'), 'success')
    return data.models
  },
  {
    onBusinessError: (error) => {
      error.handle()
      alert(l.translate('Failed to delete fields'), 'danger')
    },
  },
)

export const bulkFieldsRestore = createAsyncAction(
  `${namespace}/bulkFieldsRestore`,
  async (ids: number[]) => {
    const { data } = await UserFieldsApi.restoreBulk({ body: { ids } })
    alert(l.translate('Fields restored'), 'success')
    return data.models
  },
  {
    onBusinessError: (error) => {
      error.handle()
      alert(l.translate('Failed to restore fields'), 'danger')
    },
  },
)

export const moveToFolderBulkUserField = createAsyncAction(
  `${namespace}/moveToFolderBulkUserField`,
  async (
    {
      fieldIds,
      folderId,
      oldFolderId,
    }: {
      fieldIds: number[]
      folderId: number
      oldFolderId: number
    },
    { getState, dispatch },
  ) => {
    await UserFieldsApi.moveToFolderBulk({ body: { ids: fieldIds, folder_id: folderId } })

    alert(l.translate('Fields moved to folder!'), 'success')
    const searchValue = getUserFieldSearchValue(getState())
    if (searchValue) {
      dispatch(fetchUserFields({ activeOnly: false, query: searchValue }))
    } else if (oldFolderId) {
      dispatch(fetchUserFields({ activeOnly: true, path: `/${oldFolderId}` }))
    } else {
      dispatch(fetchUserFields({ activeOnly: false, path: '/' }))
    }
  },
)

export const moveToFolderUserField = createAsyncAction(
  `${namespace}/moveToFolderUserField`,
  async ({ field, folderId }: { field: UserField; folderId: number }, { getState, dispatch }) => {
    await UserFieldsApi.moveToFolder({ body: { field_id: field.field_id, folder_id: folderId } })

    alert(l.translate('Field moved to folder!'), 'success')
    const searchValue = getUserFieldSearchValue(getState())
    if (searchValue) {
      dispatch(fetchUserFields({ activeOnly: false, query: searchValue }))
    } else {
      dispatch(
        fetchUserFields({
          activeOnly: Boolean(field.folder_id),
          path: field.folder_id ? `/${field.folder_id}` : '/',
        }),
      )
    }
  },
)
